// Base API Response structure
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// ─── Billing types ───────────────────────────────────────────────────────────

export interface Plan {
  id: string;
  stripe_price_id: string;
  name: string;
  billing_interval: 'month' | 'year';
  amount_cents: number;
  currency: 'aud';
  trial_period_days: number;
  features: string[];
}

export interface SubscriptionStatus {
  active: boolean;
  status: string;
  planId: string | null;
  trialEnd: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface CheckoutSessionResponse {
  url: string;
  id: string;
}

export interface PortalSessionResponse {
  url: string;
}

// ─── Family / billing account types ─────────────────────────────────────────

export interface BillingMember {
  user_id: string;
  relationship: string;
  name: string;
  email: string;
}

export interface BillingAccount {
  id: string;
  type: string;
  [key: string]: any;
}

export interface BillingAccountResponse {
  account: BillingAccount;
  members: BillingMember[];
}

// Pagination
export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// User Role Enum
export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ORG_ADMIN = 'org_admin', 
  STUDENT = 'student'
}

// User related types
export interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  role: UserRole;
  studentId?: string;
  organisationId?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateUserRequest {
  email: string;
  name: string;
  password: string;
  role?: UserRole;
  organisationId?: string;
  referralCode?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

// Task types (keeping minimal for frontend use)
export interface Task {
  id: string;
  title: string;
  description?: string;
  completed: boolean;
  userId: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateTaskRequest {
  title: string;
  description?: string;
}

export interface UpdateTaskRequest {
  title?: string;
  description?: string;
  completed?: boolean;
}

// Test Management Types
export type TestCategory = 'reading' | 'mathematical' | 'thinking_skill';

export interface Question {
  id: string;
  type: string;
  title: string;
  instructions: any;
  timeLimit?: number;
  difficulty: string;
  content: any;
}

export interface TestSession {
  sessionId: string;
  category: TestCategory;
  totalQuestions: number;
  timeLimit: number;
  startedAt: string;
  questions: Question[];
}

export interface TestAnswer {
  questionId: string;
  answer: any;
}

export interface TestResult {
  sessionId: string;
  totalQuestions: number;
  answeredQuestions: number;
  correctAnswers: number;
  scorePercentage: number;
  timeSpent: number;
  results: {
    questionId: string;
    userAnswer: any;
    isCorrect: boolean;
    correctAnswer: any;
  }[];
  // Per-skill breakdown + LLM insights for the results report (ROO-131).
  breakdown?: { area: string; label?: string; correct: number; total: number; percent: number }[];
  analysis?: {
    summary?: string;
    strengths?: string[];
    improvements?: string[];
    suggestions?: { title: string; detail: string }[];
    source?: 'llm' | 'rule-based';
  };
}

export interface TestCategoryInfo {
  id: TestCategory;
  name: string;
  config: {
    questionsPerTest: number;
    timeLimit: number;
  };
}

export interface StartTestRequest {
  category: TestCategory;
  difficulty?: 'easy' | 'medium' | 'hard';
}

export interface SubmitTestRequest {
  sessionId: string;
  answers: TestAnswer[];
}

// ─── Referral types ──────────────────────────────────────────────────────────

export interface ReferralStats {
  invited: number;
  signedUp: number;
  qualified: number;
}

export interface ReferralCredits {
  grantedMonths: number;
  redeemedMonths: number;
  revokedMonths: number;
  availableMonths: number;
}

export interface ReferralData {
  code: string;
  link: string;
  stats: ReferralStats;
  credits: ReferralCredits;
}

// API endpoints constants
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/api/auth/login',
    REGISTER: '/api/auth/register',
    REFRESH: '/api/auth/refresh',
    LOGOUT: '/api/auth/logout',
    PROFILE: '/api/auth/profile',
    GOOGLE_LOGIN: '/api/auth/google',
  },
  USERS: {
    LIST: '/api/users',
    CREATE: '/api/users',
    GET: (id: string) => `/api/users/${id}`,
    UPDATE: (id: string) => `/api/users/${id}`,
    DELETE: (id: string) => `/api/users/${id}`,
  },
  TASKS: {
    LIST: '/api/tasks',
    CREATE: '/api/tasks',
    GET: (id: string) => `/api/tasks/${id}`,
    UPDATE: (id: string) => `/api/tasks/${id}`,
    DELETE: (id: string) => `/api/tasks/${id}`,
  },
  TESTS: {
    CATEGORIES: '/api/tests/categories',
    START: '/api/tests/start',
    SESSION: (sessionId: string) => `/api/tests/session/${sessionId}`,
    SUBMIT: '/api/tests/submit',
  },
  ANALYTICS: {
    CREATE_EVENT: '/api/analytics/events',
  },
  BILLING: {
    PLANS: '/api/billing/plans',
    SUBSCRIPTION: '/api/billing/subscription',
    CHECKOUT_SESSION: '/api/billing/checkout-session',
    PORTAL_SESSION: '/api/billing/portal-session',
  },
  REFERRALS: {
    ME: '/api/referrals/me',
    TRACK_INVITE: '/api/referrals/track-invite',
  },
} as const;